<script setup lang="ts">
import { NATIVE_HOST } from '@@/bits/url'
import { OzBaseButtonHrefMode } from '@@/library/v4/components/OzBaseButton.vue'
import OzPadletCraneIcon, {
  OzPadletCraneIconSizePreset as SizePreset,
} from '@@/library/v4/components/OzPadletCraneIcon.vue'

const {
  href = `https://${NATIVE_HOST}`,
  sizePreset = SizePreset.W24px,
  hrefMode = OzBaseButtonHrefMode.Default,
} = defineProps<{
  /**
   * URL where the logo points to.
   */
  href?: string

  /**
   * Size preset.
   */
  sizePreset?: SizePreset

  /**
   * See `OzBaseButton`.
   */
  hrefMode?: OzBaseButtonHrefMode
}>()

const emit = defineEmits<{
  (e: 'click', event: MouseEvent): void
}>()

function click(event: MouseEvent): void {
  if (hrefMode !== OzBaseButtonHrefMode.Default) event.preventDefault()
  if (hrefMode === OzBaseButtonHrefMode.NewTab && href) {
    window.open(href, '_blank')
  }
  if (hrefMode === OzBaseButtonHrefMode.HistoryPushState) {
    // Store the url in the history state so you can read it later
    window?.history?.pushState({ url: href }, '', href)
  }

  /**
   * Click event
   * @property {MouseEvent} event click event
   */
  emit('click', event)
}
</script>

<script lang="ts">
export { OzBaseButtonHrefMode as OzPadletLogoHrefMode } from '@@/library/v4/components/OzBaseButton.vue'
export { OzPadletCraneIconSizePreset as OzPadletCraneSizePreset } from '@@/library/v4/components/OzPadletCraneIcon.vue'
export default {}
</script>

<!-- eslint-disable vuejs-accessibility/anchor-has-content -->
<template>
  <a
    :class="[
      'flex',
      'items-center',
      'group',

      // Focus styles
      'padlet-crane',
      sizePreset === SizePreset.W24px && 'w-6 h-6',
      sizePreset === SizePreset.W28px && 'w-7 h-7',
    ]"
    :href="href"
    data-testid="padletLogo"
    @click="click"
  >
    <OzPadletCraneIcon :size-preset="sizePreset" />
  </a>
</template>

<style lang="postcss" scoped>
.padlet-crane:focus-visible {
  position: relative;
  outline: none;
}

.padlet-crane:focus-visible::after {
  content: '';
  position: absolute;
  pointer-events: none;
  outline-style: solid;
  inset-block: calc(var(--logo-height) / 2 * -1);
  inset-inline: calc(var(--logo-width) / 6 * -1);
}

.padlet-crane-light:focus-visible::after {
  outline-color: theme('colors.grape.500');
}

.padlet-crane-dark:focus-visible::after {
  outline-color: theme('colors.canary.500');
}

.padlet-crane-auto:focus-visible::after {
  outline-color: theme('colors.grape.500');

  @media (prefers-color-scheme: dark) {
    outline-color: theme('colors.canary.500');
  }
}
</style>
